import { useEffect, useState } from 'react';
import { useDataProvider, useGetOne, useNotify, useRefresh } from 'react-admin';
import { Button, Checkbox, FormControlLabel } from '@mui/material';
import { ExternalLink } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Funeral } from '../../types';
import { getToken } from '../../authentication/authProvider';
import {
  CARRIERS,
  CARRIER_LABELS,
  Carrier,
  carrierTrackingUrl,
  detectCarrier,
  isCarrier,
  isMissingColumnError,
  normalizeTrackingNumber,
} from './urnTracking';
import {
  describeSendTime,
  isWithinSendWindow,
  nextSendTime,
} from '../../lib/familySendWindow';

/**
 * Where the urn is actually bought. Staff open this, order the piece, and paste
 * back the supplier's tracking number — so the one screen carries the link, the
 * number and the status rather than scattering them.
 */
const SUPPLIER_URLS: Record<'blue' | 'beige', string> = {
  blue: 'https://faunamemorials.com/en-ca/products/laurel-urn?variant=49690530775338',
  beige: 'https://faunamemorials.com/en-ca/products/laurel-urn?variant=49989784109354',
};

/** The product page with no variant chosen, for a family who hasn't decided. */
const SUPPLIER_URL_ANY = 'https://faunamemorials.com/en-ca/products/laurel-urn';

/**
 * Above this, the standard Laurel urn may not hold the ashes.
 *
 * The rule of thumb is roughly a cubic inch of capacity per pound of body
 * weight. Staff are warned rather than blocked: the supplier's sizing is the
 * authority, and a warning at the moment of ordering is what stops the urn
 * arriving too small days later.
 */
const LARGER_URN_OVER_LB = 215;

/**
 * Pounds, from a free-text field. Production rows are bare numbers ("180"),
 * but the column is text, so a stray unit should not be read as a weight.
 */
function weightInPounds(raw?: string | null): number | null {
  if (!raw) return null;
  const match = /(\d+(?:\.\d+)?)/.exec(raw);
  if (!match) return null;
  const value = Number(match[1]);
  if (!Number.isFinite(value) || value <= 0) return null;
  return /kg/i.test(raw) ? value * 2.2046 : value;
}

const apiBase = () => {
  const apiUrl = process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001';
  return apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
};

/**
 * When the tracking text will go, or null for now. Unlike the courier's day-of
 * text, an urn tracking number is still true on Monday, so outside the weekday
 * window it is held rather than dropped.
 */
const familyTextTiming = (now: Date = new Date()): string | null => {
  if (isWithinSendWindow(now)) return null;
  return `Will send ${describeSendTime(nextSendTime(now), now)}, not now.`;
};

type UrnSavedResult =
  | {
      status:
        | 'sent'
        | 'already_sent'
        | 'no_tracking'
        | 'no_phone'
        | 'failed'
        | 'skipped'
        | 'unavailable';
    }
  | { status: 'deferred'; sendAt: string };

/**
 * Ordering the urn, in one place.
 *
 * Opened from two places that were previously separate actions: the "Order Urn"
 * step on the case timeline, and the tracking-number row on the urn card. They
 * are the same job — go and buy it, then record what came back — so they are
 * the same dialog.
 *
 * Marking it ordered is the confirm. The tracking number is optional because
 * the supplier does not always give one at order time; it can be added by
 * reopening this.
 */
export const UrnOrderDialog = ({
  record,
  open,
  onOpenChange,
}: {
  record: Funeral;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) => {
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const notify = useNotify();
  const [tracking, setTracking] = useState('');
  const [carrier, setCarrier] = useState<Carrier | ''>('');
  // Once staff pick a carrier by hand, a later edit to the number must not
  // snap it back to the guess.
  const [carrierTouched, setCarrierTouched] = useState(false);
  // On by default, like the courier modal: pasting a tracking number means the
  // supplier has shipped, and the family is waiting on that box. Silence is
  // the deliberate choice.
  const [notifyFamily, setNotifyFamily] = useState(true);
  const [saving, setSaving] = useState(false);

  // The weight sits on the deceased's client record, not the funeral, and it is
  // what decides whether the standard urn is big enough.
  const { data: deceased } = useGetOne(
    'clients',
    { id: record.deceased_client_id },
    { enabled: !!record.deceased_client_id },
  );

  // Who the text goes to: the primary contact, as for every delivery text.
  const { data: primary } = useGetOne(
    'clients',
    { id: record.primary_client_id },
    { enabled: open && !!record.primary_client_id },
  );

  const existingTracking = (record.urn_tracking_number ?? '').trim();
  const storedCarrier = isCarrier(record.urn_tracking_carrier)
    ? record.urn_tracking_carrier
    : null;

  // Seed from the record each time it opens, so reopening shows what is stored
  // rather than whatever was typed and cancelled last time.
  useEffect(() => {
    if (!open) return;
    setTracking(existingTracking);
    setCarrier(storedCarrier ?? detectCarrier(existingTracking).carrier ?? '');
    setCarrierTouched(!!storedCarrier);
    setNotifyFamily(true);
  }, [open, existingTracking, storedCarrier]);

  const normalized = normalizeTrackingNumber(tracking);
  const guess = detectCarrier(normalized);
  const trackingChanged = normalized !== normalizeTrackingNumber(existingTracking);
  const alreadyTexted =
    !!normalized &&
    normalizeTrackingNumber(record.urn_notify_sent_for) === normalized;
  const trackingPageUrl = carrierTrackingUrl(carrier || null, normalized);
  const willNotify = !!normalized && !alreadyTexted && notifyFamily;
  const contact = primary as
    | { name?: string; mobile_phone_number?: string; phone_number?: string }
    | undefined;
  const primaryPhone = contact?.mobile_phone_number || contact?.phone_number;
  const timing = familyTextTiming();

  const handleTrackingChange = (value: string) => {
    setTracking(value);
    if (!carrierTouched) setCarrier(detectCarrier(value).carrier ?? '');
  };

  const alreadyOrdered =
    !!record.urn_order_status && record.urn_order_status !== 'to_order';
  const colour = record.urn_colour
    ? record.urn_colour === 'blue'
      ? 'Blue'
      : 'Beige'
    : null;
  // Straight to the right variant when we know it: picking the wrong colour at
  // the supplier is a second order and a second wait.
  const supplierUrl = record.urn_colour
    ? SUPPLIER_URLS[record.urn_colour]
    : SUPPLIER_URL_ANY;
  const pounds = weightInPounds(
    (deceased as { weight?: string | null } | undefined)?.weight,
  );
  const needsLargerUrn = pounds !== null && pounds > LARGER_URN_OVER_LB;

  const handleConfirm = async () => {
    setSaving(true);
    try {
      const data = {
        // Only ever moves it forward to 'ordered'. A later stage — shipped,
        // received — is set on the urn card and must not be walked back by
        // someone reopening this to paste a tracking number.
        urn_order_status: alreadyOrdered ? record.urn_order_status : 'ordered',
        urn_tracking_number: normalized || null,
        urn_tracking_carrier: normalized ? carrier || null : null,
      };
      try {
        await dataProvider.update('funerals', {
          id: record.id,
          data,
          previousData: record,
        });
      } catch (error) {
        // Live before the migration that adds urn_tracking_carrier: save the
        // rest. The server then records the tracking and texts nobody.
        if (!isMissingColumnError(error)) throw error;
        const { urn_tracking_carrier: _carrier, ...withoutCarrier } = data;
        await dataProvider.update('funerals', {
          id: record.id,
          data: withoutCarrier,
          previousData: record,
        });
      }

      // The feed entry and the family text are the server's: it reads the
      // number back from the case, so what is texted is what was just saved.
      let familyNote = '';
      let warn = false;
      if (normalized && (trackingChanged || willNotify)) {
        try {
          const response = await fetch(
            `${apiBase()}/urn-tracking/funeral/${record.id}/saved`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${await getToken()}`,
              },
              body: JSON.stringify({ trackingChanged, notifyFamily: willNotify }),
            },
          );
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const result = (await response.json()) as UrnSavedResult;
          if (result.status === 'sent') familyNote = ' — family texted';
          else if (result.status === 'deferred') {
            familyNote = ` — family will be texted ${describeSendTime(
              new Date(result.sendAt),
            )}`;
          } else if (result.status === 'no_phone') {
            familyNote = ' — no phone on the primary contact, family not texted';
            warn = true;
          } else if (result.status === 'failed') {
            familyNote = ' — the family text failed and will be retried';
            warn = true;
          } else if (result.status === 'unavailable') {
            familyNote = ' — urn texts are not switched on yet, family not texted';
            warn = true;
          }
        } catch (error) {
          console.error('Urn tracking follow-up failed:', error);
          if (willNotify) {
            familyNote = ' — but the family text could not be sent';
            warn = true;
          }
        }
      }

      notify(
        `${alreadyOrdered ? 'Urn order updated' : 'Urn marked as ordered'}${familyNote}`,
        { type: warn ? 'warning' : 'success' },
      );
      onOpenChange(false);
      refresh();
    } catch (error) {
      console.error('Error saving the urn order:', error);
      notify('Could not save the urn order', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !saving && onOpenChange(next)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {alreadyOrdered ? 'Urn order' : 'Order the urn'}
          </DialogTitle>
          <DialogDescription>
            {colour
              ? `The family chose the ${colour} urn — order that variant.`
              : 'The family has not chosen a colour yet; check with them before ordering.'}
          </DialogDescription>
        </DialogHeader>

        <a
          href={supplierUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-primary underline text-sm"
        >
          <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
          {colour
            ? `Open the ${colour.toLowerCase()} urn at Fauna Memorials`
            : 'Open the urn at Fauna Memorials'}
        </a>

        {/* Capacity is roughly a cubic inch per pound, so a heavier person can
            outgrow the standard piece. Warned here rather than blocked: the
            supplier's sizing is the authority, and the moment of ordering is
            the last point where this costs nothing to fix. */}
        {needsLargerUrn && (
          <div className="rounded border-l-2 border-amber-500 bg-amber-50 dark:bg-amber-950/30 px-2.5 py-2 text-[13px] text-foreground">
            <span className="font-medium">
              {`${Math.round(pounds ?? 0)} lb — check the size before ordering.`}
            </span>{' '}
            The standard Laurel urn may not hold these ashes, and we may need to
            order a larger one.
          </div>
        )}

        <div className="space-y-1.5">
          <span className="text-xs text-muted-foreground">
            Tracking number (optional — add it later if the supplier hasn&apos;t
            given one yet)
          </span>
          <div className="flex gap-2">
            <Input
              value={tracking}
              onChange={(e) => handleTrackingChange(e.target.value)}
              placeholder="Tracking Number"
              className="flex-1"
            />
            <select
              aria-label="Carrier"
              value={carrier}
              onChange={(e) => {
                setCarrier(e.target.value as Carrier | '');
                setCarrierTouched(true);
              }}
              className="h-9 rounded-md border border-input bg-transparent px-2 text-sm"
            >
              <option value="">Carrier…</option>
              {CARRIERS.map((c) => (
                <option key={c} value={c}>
                  {CARRIER_LABELS[c]}
                </option>
              ))}
            </select>
          </div>
          {normalized && (
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              {trackingPageUrl ? (
                <a
                  href={trackingPageUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-primary underline"
                >
                  <ExternalLink className="w-3 h-3 flex-shrink-0" />
                  Open tracking page
                </a>
              ) : (
                <span>Pick a carrier to get a tracking link.</span>
              )}
              {!carrierTouched && guess.carrier && (
                <span>
                  {guess.ambiguousWith
                    ? `Guessed ${CARRIER_LABELS[guess.carrier]} — could also be ${CARRIER_LABELS[guess.ambiguousWith]}`
                    : `Detected ${CARRIER_LABELS[guess.carrier]}`}
                </span>
              )}
            </div>
          )}
        </div>

        {normalized &&
          (alreadyTexted ? (
            <p className="text-xs text-muted-foreground">
              {`The family was texted this tracking number${
                record.urn_notify_sent_at
                  ? ` on ${new Date(record.urn_notify_sent_at).toLocaleString('en-CA', {
                      timeZone: 'America/Toronto',
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    })}`
                  : ''
              }.`}
            </p>
          ) : (
            <div>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={notifyFamily}
                    onChange={(e) => setNotifyFamily(e.target.checked)}
                  />
                }
                label={
                  <span className="text-sm">
                    {`Text the family the tracking number${
                      contact?.name ? ` (${contact.name})` : ''
                    }`}
                  </span>
                }
              />
              {notifyFamily && (
                <p className="text-xs text-muted-foreground -mt-1 ml-8">
                  {contact && !primaryPhone
                    ? 'No phone number on the primary contact — no text will be sent.'
                    : `Says the urn ships separately from the ashes, with the carrier, number and tracking link.${
                        timing ? ` ${timing}` : ''
                      }`}
                </p>
              )}
            </div>
          ))}

        <div className="flex justify-end gap-2">
          <Button size="small" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => void handleConfirm()}
            disabled={saving}
          >
            {saving ? 'Saving…' : alreadyOrdered ? 'Save' : 'Mark as ordered'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
